<template>
  <v-container fluid class="fill-height pa-0" v-if="hasValidInvoice">
    <v-row justify="center" align="center" class="ma-0">
      <v-col cols="12" sm="10" md="8" lg="6" xl="5" class="pa-4">
        <v-card class="registration-card" elevation="0" rounded="lg">
          <!-- Header with Branding -->
          <div class="card-header">
            <div class="brand-logo">
              <div class="logo-icon">
                <v-icon size="32">mdi-shield-check</v-icon>
              </div>
              <div class="brand-text">
                <span class="text-primary font-weight-bold">Kazang</span>
                <span class="text-accent">Registration</span>
              </div>
            </div>
            <v-chip color="primary" variant="flat" class="security-badge" size="small">
              <v-icon start size="16">mdi-lock</v-icon>
              Secure Process
            </v-chip>
          </div>

          <!-- Progress Stepper -->
          <div class="stepper-container">
            <div class="stepper-track">
              <div
                v-for="step in steps"
                :key="step.id"
                class="step-item"
                :class="{
                  'active': currentStep === step.id,
                  'completed': currentStep > step.id
                }"
                @click="goToStep(step.id)"
              >
                <div class="step-indicator">
                  <v-icon v-if="currentStep > step.id" size="16">mdi-check</v-icon>
                  <span v-else>{{ step.letter }}</span>
                </div>
                <div class="step-label">{{ step.label }}</div>
                <div class="step-connector" v-if="step.id < steps.length"></div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="initialLoading" class="loading-state">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <div class="text-h6 mt-4">Loading registration details...</div>
          </div>

          <!-- Form Content -->
          <div v-else class="form-content">
            <!-- Step 1: Business Information -->
            <v-slide-x-transition>
              <div v-show="currentStep === 1" class="step-content">
                <v-form ref="businessForm" v-model="businessFormValid">
                  <h3 class="step-title">
                    <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
                    Business Information
                  </h3>
                  <p class="step-subtitle">Enter your business details to get started</p>

                  <div class="form-fields">
                    <v-text-field
                      v-model="kazangSignUp.companyName"
                      :rules="requiredRules"
                      label="Company Name"
                      prepend-inner-icon="mdi-office-building"
                      variant="outlined"
                      density="comfortable"
                      required
                      :disabled="prefilledFromStore"
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.branchName"
                      :rules="requiredRules"
                      label="Branch Name"
                      prepend-inner-icon="mdi-store"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.tpin"
                      :rules="requiredRules"
                      label="TPIN"
                      prepend-inner-icon="mdi-card-account-details"
                      variant="outlined"
                      density="comfortable"
                      required
                      :disabled="prefilledFromStore"
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.cellNumber"
                      :rules="phoneRules"
                      label="Contact Number"
                      prepend-inner-icon="mdi-phone"
                      variant="outlined"
                      density="comfortable"
                      required
                      :disabled="prefilledFromStore"
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.email"
                      :rules="emailRules"
                      label="Email"
                      prepend-inner-icon="mdi-email"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.kazangUser"
                      :rules="requiredRules"
                      label="Kazang User"
                      prepend-inner-icon="mdi-account-key"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.kazangPassword"
                      :rules="requiredRules"
                      label="Kazang Password"
                      prepend-inner-icon="mdi-lock"
                      :type="showKazangPassword ? 'text' : 'password'"
                      :append-inner-icon="showKazangPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append-inner="showKazangPassword = !showKazangPassword"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>
                  </div>

                  <div class="form-actions">
                    <v-btn
                      color="primary"
                      variant="flat"
                      size="large"
                      @click="validateAndProceed(1)"
                      :disabled="!businessFormValid"
                      block
                      class="action-btn"
                    >
                      Continue to Personal Info
                      <v-icon end>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </div>
            </v-slide-x-transition>

            <!-- Step 2: Personal Information -->
            <v-slide-x-transition>
              <div v-show="currentStep === 2" class="step-content">
                <v-form ref="personalForm" v-model="personalFormValid">
                  <h3 class="step-title">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Personal Information
                  </h3>
                  <p class="step-subtitle">Tell us about yourself</p>

                  <div class="form-fields">
                    <v-text-field
                      v-model="kazangSignUp.fullName"
                      :rules="requiredRules"
                      label="Full Name"
                      prepend-inner-icon="mdi-card-account-details"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.contactNumber"
                      :rules="phoneRules"
                      label="Contact Number"
                      prepend-inner-icon="mdi-phone"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>
                  </div>

                  <div class="form-actions">
                    <v-btn
                      color="secondary"
                      variant="outlined"
                      size="large"
                      @click="currentStep = 1"
                      class="mr-4 back-btn"
                    >
                      <v-icon start>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="flat"
                      size="large"
                      @click="validateAndProceed(2)"
                      :disabled="!personalFormValid"
                      class="action-btn"
                    >
                      Continue to Login Info
                      <v-icon end>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </div>
            </v-slide-x-transition>

            <!-- Step 3: Login Information -->
            <v-slide-x-transition>
              <div v-show="currentStep === 3" class="step-content">
                <v-form ref="loginForm" v-model="loginFormValid" @submit.prevent="submitForm">
                  <h3 class="step-title">
                    <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
                    Platform Login Information
                  </h3>
                  <p class="step-subtitle">Create your platform credentials</p>

                  <v-alert
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mb-4 info-alert"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-information</v-icon>
                    </template>
                    These credentials are for logging into the platform and are different from your Kazang credentials entered in Step 1.
                  </v-alert>

                  <div class="form-fields">
                    <v-text-field
                      v-model="kazangSignUp.username"
                      :rules="usernameRules"
                      label="Platform Username"
                      prepend-inner-icon="mdi-account-circle"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="kazangSignUp.password"
                      :rules="passwordRules"
                      :type="showPassword ? 'text' : 'password'"
                      label="Platform Password"
                      prepend-inner-icon="mdi-lock"
                      :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append-inner="showPassword = !showPassword"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-text-field
                      v-model="confirmPassword"
                      :rules="confirmPasswordRules"
                      :type="showPassword ? 'text' : 'password'"
                      label="Confirm Platform Password"
                      prepend-inner-icon="mdi-lock-check"
                      variant="outlined"
                      density="comfortable"
                      required
                      color="primary"
                      bg-color="background"
                    ></v-text-field>

                    <v-checkbox
                      v-model="termsAccepted"
                      :rules="[v => !!v || 'You must accept the terms and conditions']"
                      required
                      class="terms-checkbox"
                    >
                      <template v-slot:label>
                        <div>
                          I accept the
                          <a href="#" @click.prevent="showTerms = true" class="text-primary text-decoration-none">
                            terms and conditions
                          </a>
                        </div>
                      </template>
                    </v-checkbox>
                  </div>

                  <div class="form-actions">
                    <v-btn
                      color="secondary"
                      variant="outlined"
                      size="large"
                      @click="currentStep = 2"
                      class="mr-4 back-btn"
                    >
                      <v-icon start>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <v-btn
                      color="success"
                      variant="flat"
                      size="large"
                      type="submit"
                      :loading="loading"
                      :disabled="!loginFormValid || !termsAccepted"
                      class="action-btn"
                    >
                      Complete Registration
                      <v-icon end>mdi-check</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </div>
            </v-slide-x-transition>
          </div>

          <!-- Security Badges -->
          <div class="security-badges">
            <v-chip size="small" color="success" variant="flat" prepend-icon="mdi-shield-check">
              SSL Secured
            </v-chip>
            <v-chip size="small" color="info" variant="flat" prepend-icon="mdi-encryption">
              Data Encrypted
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Terms and Conditions Dialog -->
    <v-dialog v-model="showTerms" max-width="700px" scrollable>
      <v-card class="terms-dialog">
        <v-toolbar color="primary" density="compact">
          <v-toolbar-title class="text-white">Terms and Conditions</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showTerms = false" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <div class="terms-content">
            <h4 class="text-h6 mb-3">1. Account Registration</h4>
            <p class="mb-4">
              By registering for a Kazang account, you confirm that all information provided is accurate and complete.
              You are responsible for maintaining the confidentiality of your account credentials.
            </p>

            <h4 class="text-h6 mb-3">2. Service Usage</h4>
            <p class="mb-4">
              You agree to use Kazang services in compliance with all applicable laws and regulations.
              Any unauthorized use of the platform may result in immediate account termination.
            </p>

            <h4 class="text-h6 mb-3">3. Data Protection</h4>
            <p class="mb-4">
              Your personal and business information will be processed according to our Privacy Policy.
              We implement industry-standard security measures to protect your data.
            </p>

            <h4 class="text-h6 mb-3">4. Service Availability</h4>
            <p>
              While we strive for uninterrupted service, Kazang may occasionally need to perform maintenance
              or experience technical difficulties beyond our control.
            </p>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="showTerms = false">
            I Understand
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

  <!-- Invalid Invoice Screen -->
  <v-container v-else fluid class="fill-height invalid-invoice-container">
    <v-row justify="center" align="center" class="ma-0">
      <v-col cols="12" sm="8" md="6" lg="5" xl="4" class="pa-4">
        <v-card class="error-card" elevation="0" rounded="lg">
          <div class="error-icon">
            <v-icon size="80" color="error">mdi-alert-circle-outline</v-icon>
          </div>

          <v-card-title class="text-center text-h4 mb-3">
            Invalid Registration Link
          </v-card-title>

          <v-card-text class="text-center text-body-1 mb-6">
            The registration link you used is invalid or has expired.
            Please contact our support team for assistance.
          </v-card-text>

          <div class="support-actions">
            <v-btn
              color="success"
              variant="flat"
              size="large"
              href="https://wa.me/260958168829"
              target="_blank"
              prepend-icon="mdi-whatsapp"
              block
              class="mb-3 support-btn"
            >
              WhatsApp Support
            </v-btn>

            <v-btn
              color="primary"
              variant="outlined"
              size="large"
              href="tel:+260958168829"
              prepend-icon="mdi-phone"
              block
              class="support-btn"
            >
              Call Support
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "KazangRegistration",
  data() {
    return {
      currentStep: 1,
      steps: [
        { id: 1, letter: 'B', label: 'Business' },
        { id: 2, letter: 'P', label: 'Personal' },
        { id: 3, letter: 'L', label: 'Login' }
      ],
      loading: false,
      initialLoading: true,
      showPassword: false,
      showKazangPassword: false,
      confirmPassword: '',
      termsAccepted: false,
      showTerms: false,
      hasValidInvoice: false,
      prefilledFromStore: false,

      kazangSignUp: {
        "tpin": "",
        "cellNumber": "",
        "kazangUser": "",
        "kazangPassword": "",
        "companyName": "",
        "branchName": "",
        "storeId": null,
        "username": "",
        "password": "",
        "email": "",
        "contactNumber": "",
        "fullName": ""
      },

      // Form validation
      businessFormValid: false,
      personalFormValid: false,
      loginFormValid: false,

      // Form validation rules
      requiredRules: [
        v => !!v || 'This field is required'
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      phoneRules: [
        v => !!v || 'Phone number is required',
        v => /^[0-9+\-\s()]{10,15}$/.test(v) || 'Phone number must be valid'
      ],
      usernameRules: [
        v => !!v || 'Username is required',
      ],
      passwordRules: [
        v => !!v || 'Password is required',
      ],
    }
  },
  computed: {
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.kazangSignUp.password || 'Passwords do not match'
      ]
    }
  },
  async beforeMount() {
    try {
      await this.verifyInvoiceNumber();
      if (this.hasValidInvoice) {
        await this.getStoreDetails();
      }
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.initialLoading = false;
    }
  },
  methods: {
    async verifyInvoiceNumber() {
      const invoiceNumber = this.$route.query.invoiceNumber;
      if (!invoiceNumber) {
        this.hasValidInvoice = false;
        return;
      }

      try {
        const result = await this.$store.dispatch("checkInvoiceNumber", invoiceNumber);
        this.hasValidInvoice = result.containsAddon;
      } catch (error) {
        console.error('Error verifying invoice number:', error);
        this.hasValidInvoice = false;
      }
    },

    async getStoreDetails() {
      const storeId = this.$route.query.storeID;
      if (!storeId) {
        console.log("No storeId provided");
        return;
      }

      try {
        const res = await this.$store.dispatch("getStoreByID", storeId);
        if (res) {
          this.kazangSignUp.storeId = parseInt(storeId);
          this.kazangSignUp.companyName = res.businessName || '';
          this.kazangSignUp.branchName = res.branchName || '';
          this.kazangSignUp.tpin = res.tpin || '';
          this.kazangSignUp.cellNumber = res.cellNumber || '';
          // Ensure username and password fields are not pre-filled as they are different for Kazang and user
          this.kazangSignUp.kazangUser = '';
          this.kazangSignUp.kazangPassword = '';
          this.kazangSignUp.username = '';
          this.kazangSignUp.password = '';
          // this.prefilledFromStore = true;
        }
      } catch (error) {
        console.error('Error fetching store details:', error);
      }
    },

    goToStep(step) {
      if (step <= this.currentStep || step === 1) {
        this.currentStep = step;
      }
    },

    validateAndProceed(step) {
      const formRefs = {
        1: 'businessForm',
        2: 'personalForm'
      };

      const formRef = this.$refs[formRefs[step]];
      if (formRef && formRef.validate()) {
        this.currentStep = step + 1;
      }
    },

    async submitForm() {
      if (!this.$refs.loginForm.validate() || !this.termsAccepted) {
        return;
      }

      if (this.loading) return;
      this.loading = true;

      try {
        const response = await this.$store.dispatch("registerKazangUser", this.kazangSignUp);
        responses.showSuccess('Registration successful! Redirecting to login...')
        // this.$toast.success();
        setTimeout(() => {
          this.$router.push({ name: 'LoginForm' });
        }, 1500);
      } catch (error) {
        console.error("Error registering Kazang user:", error);
        this.$toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>

<style scoped lang="scss">
.registration-card {
  overflow: visible;
  padding: 32px;
  position: relative;
  border: none;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 10px 30px rgba(var(--v-theme-on-surface-rgb), 0.08) !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgba(var(--v-theme-primary-rgb), 0.8) 100%);

  .v-icon {
    color: rgb(var(--v-theme-on-primary)) !important;
  }
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  span:first-child {
    font-size: 1.5rem;
    font-weight: 700;
  }

  span:last-child {
    font-size: 1rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface-variant));
  }
}

.security-badge {
  font-weight: 500;
}

.stepper-container {
  margin: 0 auto 40px;
  max-width: 600px;
}

.stepper-track {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  cursor: pointer;
  flex: 1;
}

.step-indicator {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  background-color: rgba(var(--v-theme-primary-rgb), 0.1);
  color: rgba(var(--v-theme-primary-rgb), 0.8);
  border: 2px solid rgba(var(--v-theme-primary-rgb), 0.2);
  transition: all 0.3s ease;
}

.step-item.active .step-indicator {
  background-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  border-color: rgb(var(--v-theme-primary));
  transform: scale(1.1);
  box-shadow: 0 0 0 6px rgba(var(--v-theme-primary-rgb), 0.2);
}

.step-item.completed .step-indicator {
  background-color: rgb(var(--v-theme-success));
  color: rgb(var(--v-theme-on-success));
  border-color: rgb(var(--v-theme-success));
}

.step-label {
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface-variant));
  text-align: center;
  transition: all 0.3s ease;
}

.step-item.active .step-label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.step-item.completed .step-label {
  color: rgb(var(--v-theme-success));
}

.step-connector {
  position: absolute;
  top: 18px;
  right: -50%;
  width: 100%;
  height: 2px;
  background-color: rgba(var(--v-theme-on-surface-rgb), 0.1);
  z-index: 1;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 0;
}

.step-content {
  padding: 16px 0;
}

.step-title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.step-subtitle {
  margin-bottom: 24px;
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.95rem;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.info-alert {
  border: none;
  border-radius: 12px;
  background-color: rgba(var(--v-theme-primary-rgb), 0.05) !important;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
}

.action-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.back-btn {
  border-radius: 8px;
  text-transform: none;
}

.security-badges {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid rgba(var(--v-theme-on-surface-rgb), 0.08);
  flex-wrap: wrap;
}

.terms-checkbox {
  margin-top: 8px;
}

.error-card {
  padding: 40px 32px;
  text-align: center;
  border: none;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 10px 30px rgba(var(--v-theme-on-surface-rgb), 0.08) !important;
  border-radius: 16px;
}

.error-icon {
  margin-bottom: 24px;
}

.support-actions {
  margin-top: 32px;
}

.support-btn {
  border-radius: 8px;
}

.invalid-invoice-container {
  background: rgb(var(--v-theme-background));
}

.terms-dialog {
  border-radius: 16px;
  overflow: hidden;
}

/* Animations */
.registration-card {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .registration-card {
    padding: 24px;
  }

  .brand-text {
    span:first-child {
      font-size: 1.25rem;
    }

    span:last-child {
      font-size: 0.875rem;
    }
  }
}

@media (max-width: 600px) {
  .registration-card {
    padding: 20px;
    margin: 8px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 24px;
  }

  .stepper-track {
    &::before {
      top: 12px;
    }
  }

  .step-indicator {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
  }

  .step-label {
    font-size: 0.75rem;
  }

  .step-connector {
    top: 16px;
  }

  .form-actions {
    flex-direction: column-reverse;
    gap: 12px;

    .v-btn {
      width: 100%;
      margin-right: 0 !important;
    }
  }

  .step-title {
    font-size: 1.1rem;
    margin-bottom: 16px;
  }
}
</style>
